import type { components } from "@/api-schema";
import type {
  MappedBranch,
  MappedTransportCompany,
  MappedUser,
  RelocationOrderAdminFormSchemaType,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";
import { getAvailableStatuses } from "@/modules/fit/internal/relocation-order/admin/_helpers/get-available-statuses";
import { useRelocationOrderFormOptions } from "@/modules/fit/internal/relocation-order/admin/_hooks/use-relocation-order-form-options";
import type { ComboboxPaginationState } from "@/components/ui/combobox";
import { useMemo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";

type Vehicle = components["schemas"]["Vehicle"];
type Branch = components["schemas"]["Branch"];
type RelocationOrderStatus = components["schemas"]["RelocationOrder"]["status"];

export interface RelocationOrderAdminFormOptions {
  mappedBranches: MappedBranch[];
  mappedDestinationBranches: MappedBranch[];
  mappedTransportCompanies: MappedTransportCompany[];
  mappedTransportCompanyUsers: MappedUser[];
  mappedOrderedByUsers: MappedUser[];
  branchComboboxState: ComboboxPaginationState;
  companyComboboxState: ComboboxPaginationState;
  driverComboboxState: ComboboxPaginationState;
  orderedByComboboxState: ComboboxPaginationState;
  statuses: RelocationOrderStatus[];
  selectedOriginBranch?: Branch;
  selectedCurrentBranch?: Branch;
  selectedDestinationBranch?: Branch;
}

interface UseRelocationOrderAdminFormStateParams {
  form: UseFormReturn<RelocationOrderAdminFormSchemaType>;
  vehicle?: Vehicle;
  /** The status of the order being edited. It stays selectable next to the selected status. */
  savedStatus?: RelocationOrderStatus;
}

export function useRelocationOrderAdminFormState({
  form,
  vehicle,
  savedStatus,
}: UseRelocationOrderAdminFormStateParams): { options: RelocationOrderAdminFormOptions; isLoading: boolean } {
  const selectedTransportMethod = useWatch({ control: form.control, name: "transportMethod" });
  const selectedTransportCompanyUuid = useWatch({ control: form.control, name: "transportCompany" });
  const selectedStatus = useWatch({ control: form.control, name: "status" });
  const selectedOriginBranchUuid = useWatch({ control: form.control, name: "originBranch" });
  const selectedCurrentBranchUuid = useWatch({ control: form.control, name: "currentBranch" });
  const selectedDestinationBranchUuid = useWatch({ control: form.control, name: "destinationBranch" });
  const selectedPickedUpByUuid = useWatch({ control: form.control, name: "pickedUpBy" });
  const selectedOrderedByUuid = useWatch({ control: form.control, name: "orderedBy" });

  const { isLoading, ...formOptions } = useRelocationOrderFormOptions({
    selectedTransportCompanyUuid: selectedTransportCompanyUuid ?? undefined,
    selectedVehicleOriginUuid: selectedOriginBranchUuid ? vehicle?.currentBranch?.uuid : undefined,
    selectedOriginBranchUuid: selectedOriginBranchUuid || undefined,
    selectedCurrentBranchUuid: selectedCurrentBranchUuid || undefined,
    selectedStatus,
    selectedDestinationBranchUuid,
    selectedPickedUpByUuid: selectedPickedUpByUuid ?? undefined,
    selectedOrderedByUuid: selectedOrderedByUuid ?? undefined,
  });

  const statuses = useMemo(
    () =>
      getAvailableStatuses({
        keepStatuses: [savedStatus, selectedStatus].filter(Boolean),
        vehicleBlock: vehicle?.vehicleBlock,
        transportMethod: selectedTransportMethod,
      }),
    [savedStatus, selectedStatus, vehicle?.vehicleBlock, selectedTransportMethod],
  );

  return { options: { ...formOptions, statuses }, isLoading };
}
